import { Dialog, Grid, MenuItem, Select, Typography } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import { cloneDeep } from 'lodash';
import { MTableToolbar } from 'material-table';
import React from 'react';

import { ReportingEventTypes, track } from '~/src/dash/analytics/analytics';
import {
  createReport,
  deleteReport,
  getFWPublisherIDs,
  getPublisherAdQualityStatus,
  IReport,
  IUser,
  mapReportTypeToText,
  queryReports,
  ReportActive,
  ReportInactive,
  ReportType,
  SuperOnlyActive,
  updateReport,
} from '~/src/dash/api/console-api';
import { Panel, Panels } from '~/src/dash/components/layout';
import { IListTableColumn, IReportRowData, ListTable } from '~/src/dash/components/list-table';
import { MenuButton } from '~/src/dash/components/menu-button';
import { ModalSelectTable } from '~/src/dash/components/modal-select-table';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { IStore } from '~/src/dash/reducers/store';
import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteBidderReportsList,
  RouteReportExisting,
  RouteReportNew,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { parseDate } from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import {
  allowedEmptyIPermissionCheck,
  IPermissionCheck,
  IS_AUTHORIZED,
} from '~/src/dash/utils/permission_service';
import {
  FrequencyDaily,
  FrequencyMonthly,
  FrequencyWeekly,
  getReportHeaderBidderTypes,
  period,
} from '~/src/dash/utils/report-fields';
import {
  checkUserInfoMasked,
  hasRoleSuperUser,
  isSuper,
  ROLE_ID_SUPER,
} from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { FilterActiveChip } from '~/src/dash/views/campaign/campaign-settings-list-view';
import { RowButton } from '~/src/dash/views/component-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  loadingReports: boolean;
  updatingReports: boolean;
  openRecipientsDialog: boolean;
  hasPublisherEnabledBrandRecognition: boolean;
  hasPublisherEnabledLanguageDetection: boolean;

  reports: IReport[];
  report?: IReport;

  // report filters
  reportStatusFilter: string;
  reportTypeFilter: ReportType;
  reportUserFilter: string;
  reportUserIDFilter?: string;
  selectedReportID: string;
  selectedReportName: string;

  snackbarMessage: string;
  snackbarError: boolean;

  readAuthorization: IPermissionCheck;
  writeAuthorization: IPermissionCheck;
  fwPublisherIDs: number[];
}

export const ReportsList = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      loadingReports: true,
      updatingReports: false,
      openRecipientsDialog: false,
      hasPublisherEnabledBrandRecognition: false,
      hasPublisherEnabledLanguageDetection: false,

      reports: [] as IReport[],
      report: undefined,

      // report filters
      reportStatusFilter: 'Show All',
      reportTypeFilter: 'Show All',
      reportUserFilter: 'Current User',
      reportUserIDFilter: isSuper(this.props.user.user) ? '0' : this.props.user.user.ID,
      selectedReportID: '',
      selectedReportName: '',

      snackbarMessage: '',
      snackbarError: false,

      readAuthorization: allowedEmptyIPermissionCheck,
      writeAuthorization: allowedEmptyIPermissionCheck,
      fwPublisherIDs: [],
    };

    componentDidMount() {
      this.trackEvent('Report List - View');
      const readAuthorization = IS_AUTHORIZED(['read_report'], this.props.user.user);
      const writeAuthorization = IS_AUTHORIZED(['write_report'], this.props.user.user);
      this.setState({
        readAuthorization,
        writeAuthorization,
      });

      if (readAuthorization.isAuthorized) {
        this.queryReports();
      }

      this.getPublisherIDsUsingFW();
    }

    getPublisherIDsUsingFW = () => {
      getFWPublisherIDs()
        .then((fwPublisherIDs: number[]) => {
          this.setState({
            fwPublisherIDs,
          });
        })
        .catch(() => {
          this.setState({
            fwPublisherIDs: [],
          });
        });
    };

    getPublisherAdQualityStatus = () => {
      getPublisherAdQualityStatus(parseInt(this.props.session.publisher.ID, 10))
        .then((publisherAdQualityStatus) => {
          const [hasPublisherEnabledBrandRecognition, hasPublisherEnabledLanguageDetection] =
            publisherAdQualityStatus;
          this.setState({
            hasPublisherEnabledBrandRecognition,
            hasPublisherEnabledLanguageDetection,
          });
        })
        .catch(() => {
          this.setState({
            hasPublisherEnabledBrandRecognition: false,
            hasPublisherEnabledLanguageDetection: false,
          });
        });
    };

    queryReports = () => {
      this.setState({
        loadingReports: true,
      });
      queryReports({
        query: {
          publisher_id: this.props.session.publisher.ID,
        },
      })
        .then((reports: IReport[]) => {
          this.setState({
            loadingReports: false,
            reports,
          });
        })
        .catch((err: Error) => {
          this.setState({
            snackbarMessage: err.toString(),
            snackbarError: true,
            loadingReports: false,
          });
        });
    };

    // ********************************
    // ******** CRUD FUNCTIONS ********
    // ********************************
    createReport = () => {
      this.trackEvent('Report List - Create Report');
      navigate(
        RouteReportNew,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    updateReport = (report: IReport) => {
      const { reports } = this.state;
      updateReport({
        query: {
          publisher_id: this.props.session.publisher.ID,
          report_id: report.ID,
          report,
        },
      }).then(
        (updatedReport: IReport) => {
          const newReports = cloneDeep(reports);
          for (let idx = 0; idx < newReports.length; idx++) {
            if (newReports[idx].ID === updatedReport.ID) {
              newReports[idx] = report;
              break;
            }
          }
          this.setState({
            reports: newReports,
            snackbarMessage: 'Successfully updated report',
          });
        },
        () => {
          this.setState({
            snackbarMessage: `Failed to update report: [${report.ID}] ${report.Name}`,
            snackbarError: true,
          });
        },
      );
    };

    cloneReport = (reportID: string) => {
      const { reports } = this.state;
      const { session } = this.props;
      const foundReport = reports.find((report: IReport) => report.ID === reportID);
      if (foundReport) {
        createReport({
          query: {
            publisher_id: session.publisher.ID,
            report: Object.assign(foundReport, {
              ID: undefined,
              CreatedAt: undefined,
              UpdatedAt: undefined,
              DeletedAt: undefined,
            }),
          },
        }).then(
          (newReport) => {
            navigate(RouteReportExisting, extractRouteParams(this.props), {
              reportID: newReport.ID,
            });
          },
          () => {
            this.setState({
              snackbarMessage: `Failed to clone report: ${foundReport.Name} [${foundReport.ID}]`,
              snackbarError: true,
            });
          },
        );
      } else {
        this.setState({
          snackbarMessage: `Failed to clone report`,
          snackbarError: true,
        });
      }
    };

    deleteReport = (reportID: string) => {
      const { reports } = this.state;
      const { session } = this.props;

      this.setState(
        {
          updatingReports: true,
        },
        () => {
          deleteReport({
            query: {
              publisher_id: session.publisher.ID,
              report_id: reportID,
            },
          })
            .then(() => {
              const filteredReports = reports.filter((report: IReport) => report.ID !== reportID);
              this.setState({
                reports: filteredReports,
                snackbarMessage: 'Successfully deleted report',
                snackbarError: false,
                updatingReports: false,
              });
            })
            .catch((err) => {
              console.error(err);
              this.setState({
                snackbarMessage: 'Error while deleting report',
                snackbarError: true,
                updatingReports: false,
              });
            });
        },
      );

      // the delete modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    // ********************************
    // ******* HANDLER FUNCTIONS ******
    // ********************************
    handleChangeReportStatusFilter = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({ reportStatusFilter: target.value });
    };

    handleChangeReportTypeFilter = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({ reportTypeFilter: target.value });
    };

    handleChangeReportUserFilter = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({ reportUserFilter: target.value });
    };

    handleChangeActive = (reportID: string, checked: boolean) => {
      const { reports, updatingReports } = this.state;

      if (updatingReports) {
        return;
      }

      const foundReport = reports.find((report: IReport) => reportID === report.ID);
      if (foundReport) {
        foundReport.Active = checked ? ReportActive : ReportInactive;
        this.updateReport(foundReport);
      }
    };

    handleClickReportUserIDFilter = (userID: string | undefined) => {
      this.setState({ reportUserIDFilter: userID });
    };

    handleCloseRecipientsDialog = () => {
      this.setState({ openRecipientsDialog: false });
    };

    handleOpenRecipientsDialog = (selectedReportID: string) => {
      const { reports } = this.state;
      const selectedReport =
        reports.find((report: IReport) => report.ID === selectedReportID) ||
        ({ ID: '', Name: '' } as IReport);

      this.setState(
        {
          selectedReportID,
          selectedReportName: selectedReport.Name,
        },
        () => {
          this.setState({
            openRecipientsDialog: true,
          });
        },
      );
    };

    handleSetUpdating = (updating: boolean) => {
      this.setState({ updatingReports: updating });
    };

    handleClearFilters = () => {
      const { user } = this.props;
      this.setState({
        reportStatusFilter: 'Show All',
        reportTypeFilter: 'Show All',
        reportUserFilter: 'Current User',
        reportUserIDFilter: user.user.ID,
      });
    };

    // ********************************
    // ********* NAV FUNCTIONS ********
    // ********************************
    getReportSettingsRoute = (reportID: string): string => {
      const { reports } = this.state;
      const foundReport = reports.find((report: IReport) => report.ID === reportID);
      if (foundReport) {
        return getRoutePath(RouteReportExisting, extractRouteParams(this.props), {
          reportID,
        });
      } else {
        return getRoutePath(RouteBidderReportsList, extractRouteParams(this.props));
      }
    };

    // ********************************
    // ********* MISC FUNCTIONS *******
    // ********************************
    countActiveFilters = (): number => {
      const { reportStatusFilter, reportTypeFilter, reportUserFilter } = this.state;
      let activeFilterCount = 0;
      if (reportStatusFilter !== 'Show All') {
        activeFilterCount++;
      }
      if (reportTypeFilter !== 'Show All') {
        activeFilterCount++;
      }
      if (reportUserFilter !== 'All Users') {
        activeFilterCount++;
      }
      return activeFilterCount;
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    // generate, sort, and dedup the users for the user filter dropdown
    generateSortedUsers = (): IUser[] => {
      const { reports } = this.state;
      const { user } = this.props;

      const clonedUsers = cloneDeep(reports).map((report: IReport) => report.User);
      const regularUsers = clonedUsers.filter((user: IUser) => user.RoleID !== ROLE_ID_SUPER);
      const superUsers = clonedUsers.filter((user: IUser) => user.RoleID === ROLE_ID_SUPER);

      // update the names of super users
      for (let idx = 0; idx < superUsers.length; idx++) {
        superUsers[idx].ID = '0';
        superUsers[idx].FirstName = 'Publica';
        superUsers[idx].LastName = 'Admin';
      }

      const allUsers = regularUsers.concat(superUsers);

      // sort the users using their full name lexicographically
      allUsers.sort((user1, user2) => {
        const user1FullName = `${user1.FirstName} ${user1.LastName}`;
        const user2FullName = `${user2.FirstName} ${user2.LastName}`;
        if (user1FullName < user2FullName) {
          return -1;
        } else if (user1FullName > user2FullName) {
          return 1;
        }
        return 0;
      });

      // dedup the users and return the result
      const resultUsers = [];
      const seenUsers = new Set();
      for (const sortedUser of allUsers) {
        if (!seenUsers.has(sortedUser.ID) && sortedUser.ID !== user.user.ID) {
          resultUsers.push(sortedUser);
          seenUsers.add(sortedUser.ID);
        }
      }

      return resultUsers;
    };

    trackEvent = (eventTitle: ReportingEventTypes) => {
      const is_super_user = hasRoleSuperUser(this.props.user.user);
      if (!is_super_user) {
        track(
          eventTitle,
          {
            is_super_user,
            id: this.props.user.user.ID,
          },
          this.props.user.user,
        );
      }
    };

    filterReports = (reports: IReport[]): IReport[] => {
      const { user } = this.props;
      const { reportStatusFilter, reportTypeFilter, reportUserFilter, reportUserIDFilter } =
        this.state;

      return reports.filter((report: IReport) => {
        // report status filter
        if (reportStatusFilter !== 'Show All' && reportStatusFilter !== report.Active) {
          return false;
        }

        // report type filter
        if (
          reportTypeFilter !== 'Show All' &&
          reportTypeFilter !== mapReportTypeToText(report.ReportType)
        ) {
          return false;
        }

        // filter the reports using the current user or report user
        if (reportUserFilter !== 'All Users') {
          if (reportUserFilter !== 'Current User') {
            const parsedReportUserIDFilter = parseInt(reportUserIDFilter, 10);
            if (reportUserFilter === 'Publica Admin') {
              if (isSuper(user.user)) {
                return report.User.RoleID === ROLE_ID_SUPER;
              } else {
                return report.User.RoleID === ROLE_ID_SUPER && report.SuperOnly !== SuperOnlyActive;
              }
            } else {
              // filter is on a specific user, keep if there is a match
              const reportUserID = report.User.RoleID === ROLE_ID_SUPER ? '0' : report.UserID;
              if (isSuper(user.user)) {
                return parsedReportUserIDFilter === reportUserID;
              } else {
                return (
                  parsedReportUserIDFilter === reportUserID && report.SuperOnly !== SuperOnlyActive
                );
              }
            }
          } else {
            // filter is on the current user, keep if there is a match
            const parsedUserID = parseInt(user.user.ID, 10);
            if (isSuper(user.user)) {
              return parsedUserID === report.UserID;
            } else {
              return parsedUserID === report.UserID && report.SuperOnly !== SuperOnlyActive;
            }
          }
        }

        // current report passes through all of the filters, check super user related
        return isSuper(user.user) || (!isSuper(user.user) && report.SuperOnly !== SuperOnlyActive);
      });
    };

    compareLastUpdatedDates = (rowData1: IReportRowData, rowData2: IReportRowData) => {
      return rowData1.LastUpdated.isBefore(rowData2.LastUpdated) ? -1 : 1;
    };

    // ********************************
    // ******* RENDER FUNCTIONS *******
    // ********************************
    renderUserRow = () => {
      const { reportUserFilter } = this.state;
      const { user } = this.props;

      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2}>
              <Typography style={{ fontSize: 14, paddingLeft: 10 }}>User</Typography>
              <Grid item xs={1} />
              <Grid item xs={9}>
                <Select
                  name='reportUserFilter'
                  value={reportUserFilter}
                  onChange={this.handleChangeReportUserFilter}
                  style={{ fontSize: 14 }}
                >
                  <MenuItem
                    value={'All Users'}
                    onClick={() => this.handleClickReportUserIDFilter(undefined)}
                  >
                    All Users
                  </MenuItem>
                  <MenuItem
                    value={'Current User'}
                    onClick={() => this.handleClickReportUserIDFilter(user.user.ID)}
                  >
                    Current User
                  </MenuItem>
                  {this.generateSortedUsers().map((user: IUser) => {
                    const userFullName = `${user.FirstName} ${user.LastName}`;
                    return (
                      <MenuItem
                        value={user.RoleID === ROLE_ID_SUPER ? 'Publica Admin' : userFullName}
                        onClick={() => this.handleClickReportUserIDFilter(user.ID)}
                      >
                        {userFullName}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderStatusRow = () => {
      const { reportStatusFilter } = this.state;
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2}>
              <Typography style={{ fontSize: 14, paddingLeft: 10 }}>Schedule</Typography>
              <Grid item xs={1} />
              <Grid item xs={9}>
                <Select
                  name='reportStatusFilter'
                  value={reportStatusFilter}
                  onChange={this.handleChangeReportStatusFilter}
                  style={{ fontSize: 14 }}
                >
                  <MenuItem value={ReportActive}>Scheduled</MenuItem>
                  <MenuItem value={ReportInactive}>Not Scheduled</MenuItem>
                  <MenuItem value={'Show All'}>Show All</MenuItem>
                </Select>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderReportTypeRow = () => {
      const {
        reportTypeFilter,
        fwPublisherIDs,
        hasPublisherEnabledBrandRecognition,
        hasPublisherEnabledLanguageDetection,
      } = this.state;
      const { session, user } = this.props;

      const reportTypesWrapper = getReportHeaderBidderTypes(
        session.publisher,
        user.user,
        fwPublisherIDs,
        hasPublisherEnabledBrandRecognition,
        hasPublisherEnabledLanguageDetection,
      );
      const reportTypes = Object.keys(reportTypesWrapper).map(
        (reportType) => reportTypesWrapper[reportType],
      );

      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2}>
              <Typography style={{ fontSize: 14, paddingLeft: 10 }}>Report Type</Typography>
              <Grid item xs={1} />
              <Grid item xs={9}>
                <Select
                  name='reportTypeFilter'
                  value={reportTypeFilter}
                  onChange={this.handleChangeReportTypeFilter}
                  style={{ fontSize: 14 }}
                >
                  {reportTypes.map((reportType) => {
                    return (
                      <MenuItem value={mapReportTypeToText(reportType.ID)}>
                        {reportType.title.replace(' Report', '')}
                      </MenuItem>
                    );
                  })}
                  <MenuItem value={'Show All'}>Show All</MenuItem>
                </Select>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderFilterTitle = () => {
      const activeFilterCount = this.countActiveFilters();
      return (
        <Grid container direction='row' alignItems='center' spacing={2}>
          <Grid item>
            <Typography
              style={{ marginLeft: 10, fontSize: '0.875rem', fontWeight: 500, color: 'black' }}
            >
              Filters
            </Typography>
          </Grid>
          <Grid item>
            <FilterListIcon style={{ fontSize: '1rem', fontWeight: 500, color: 'black' }} />
          </Grid>
          {activeFilterCount !== 0 && (
            <Grid item xs={2}>
              <FilterActiveChip label={`${activeFilterCount} Active Filters`} />
            </Grid>
          )}
        </Grid>
      );
    };

    renderClearFilterRow = () => {
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2} onClick={this.handleClearFilters}>
              <Typography
                style={{
                  fontSize: 14,
                  paddingLeft: 10,
                  fontWeight: 500,
                  color: getColorTheme('secondaryButtonText'),
                }}
              >
                CLEAR FILTERS
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderFilters = () => {
      // created by dropdown
      const createdByRow = {
        closeOnClick: false,
        render: this.renderUserRow,
      };

      // status dropdown
      const statusRow = {
        closeOnClick: false,
        render: this.renderStatusRow,
      };

      // report type dropdown
      const reportTypeRow = {
        closeOnClick: false,
        render: this.renderReportTypeRow,
      };

      // clear filters
      const clearFilterRow = {
        closeOnClick: false,
        render: this.renderClearFilterRow,
      };

      const filterButtonItems = [createdByRow, statusRow, reportTypeRow, clearFilterRow];
      return (
        <MenuButton titleRender={this.renderFilterTitle} items={filterButtonItems} minWidth={650} />
      );
    };

    renderCustomToolbar = (props: any) => {
      const copyProps = {
        ...props,
        searchFieldStyle: { ...props.searchFieldStyle, marginLeft: '-48px', width: '300px' },
      };

      return (
        <Grid item>
          <Grid container direction='row' justify='space-between' alignItems='center'>
            <Grid item>
              <Grid
                container
                direction='column'
                justify='flex-start'
                alignItems='flex-start'
                spacing={0}
              >
                {/* grid with MTableToolbarWrapper class is required for auto focusing search upon loading the list table component */}
                <Grid item className='MTableToolbarWrapper'>
                  <MTableToolbar {...copyProps} />
                </Grid>
                <Grid item>{this.renderFilters()}</Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container>
                {/* if on all report page */}
                <Grid item>
                  <RowButton
                    right={
                      <ModelMediumButton
                        variant='contained'
                        color='primary'
                        style={{ paddingLeft: 15, paddingRight: 15 }}
                        onClick={this.createReport}
                      >
                        + New Report
                      </ModelMediumButton>
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderRecipientsModalToolbar = (props: any) => {
      const { selectedReportID, selectedReportName } = this.state;
      return (
        <Grid item style={{ marginLeft: -10 }}>
          <Grid container direction='column' justify='space-between'>
            <Typography style={{ fontSize: 24, fontWeight: 500, marginLeft: 22 }}>
              {`Recipients of ${selectedReportName} [${selectedReportID}]`}
            </Typography>
          </Grid>
          <MTableToolbar {...props} />
        </Grid>
      );
    };

    renderFrequency = (reportFrequency: string): string => {
      if (reportFrequency === FrequencyDaily) {
        return 'Daily';
      } else if (reportFrequency === FrequencyWeekly) {
        return 'Weekly';
      } else if (reportFrequency === FrequencyMonthly) {
        return 'Monthly';
      } else {
        return 'Unknown';
      }
    };

    renderPeriod = (reportPeriod: string): string => {
      for (const periodObj of period) {
        if (periodObj.key === reportPeriod) {
          return periodObj.text;
        }
      }
      return '';
    };

    render() {
      const {
        readAuthorization,
        writeAuthorization,
        loadingReports,
        openRecipientsDialog,
        reports,
        selectedReportID,
        snackbarError,
        snackbarMessage,
        updatingReports,
      } = this.state;
      const { session, user, classes, location } = this.props;

      if (reports.length === 0 && loadingReports) {
        return <LoadingView active={true} text='Loading reports' />;
      }

      if (!readAuthorization.isAuthorized && !writeAuthorization.isAuthorized) {
        return (
          <ModelMissingPermission
            missingPermissions={[
              ...readAuthorization.missingPermissions,
              ...writeAuthorization.missingPermissions,
            ]}
          />
        );
      }

      // column information for material table => 'field' should match name in state,
      // 'title' for column display in table, 'renderTypes' can be found in list-table.ts. If the 'renderType' doesn't exist
      // there yet, create a new 'renderType' if it can be reused or if it seems unique, set a custom 'render'
      // in the 'render' field.  Additional column props can be found at https://material-table.com/#/docs/get-started
      const listTableColumns: IListTableColumn[] = [
        {
          field: 'Active',
          title: 'Scheduled',
          renderType: 'activeswitch',
          searchable: false,
          maxWidth: '75px',
          sorting: false,
        },
        { field: 'Name', title: 'Name', renderType: 'namelink', minWidth: '250px' },
        {
          field: 'ReportType',
          title: 'Type',
          renderType: 'normal',
          searchable: false,
          minWidth: '100px',
        },
        {
          field: 'LastUpdated',
          title: 'Last Updated',
          renderType: 'date',
          searchable: false,
          maxWidth: '125px',
          customSort: this.compareLastUpdatedDates,
        },
        {
          field: 'Recipients',
          title: 'Recipients',
          renderType: 'modal2',
          searchable: false,
          width: '10%',
        },
        {
          field: 'Frequency',
          title: 'Frequency',
          render: (rowData: any) => this.renderFrequency(rowData.Frequency),
          searchable: false,
          width: '10%',
        },
        {
          field: 'Period',
          title: 'Period',
          render: (rowData: any) => this.renderPeriod(rowData.Period),
          searchable: false,
          maxWidth: '50px',
        },
        { field: 'Actions', title: 'Actions', renderType: 'deleteclone', searchable: false },
        { field: 'ID', title: 'ID', renderType: 'normal', searchable: true, hidden: true },
      ];

      const selectedReport =
        reports.find((report: IReport) => report.ID === selectedReportID) ||
        ({ Recipients: [] as string[] } as IReport);
      const filteredReports = this.filterReports(reports);

      // generate row data, the object keys here need to match the fields above
      const listTableData: IReportRowData[] = filteredReports.map((report: IReport) => {
        return {
          ID: report.ID,
          Active: report.Active === ReportActive,
          Name: checkUserInfoMasked(user.user)
            ? `demo_report_${report.ID}`
            : `${report.Name} [${report.ID}]`,
          ReportType: mapReportTypeToText(report.ReportType).replace(' Report', ''),
          LastUpdated: parseDate(report.UpdatedAt).isValid()
            ? parseDate(report.UpdatedAt)
            : parseDate(report.CreatedAt),
          Recipients: report.Recipients.length,
          Frequency: report.Frequency,
          Period: report.Period,
        };
      });

      const { pathname } = location;

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            <Grid container direction='column'>
              <Grid item style={{ width: '100%' }}>
                <ListTable
                  title={''}
                  columns={listTableColumns}
                  data={listTableData}
                  isLoading={loadingReports}
                  isUpdating={updatingReports}
                  handleChangeActive={this.handleChangeActive}
                  handleOpenModal2={this.handleOpenRecipientsDialog}
                  getItemSettingRoute={this.getReportSettingsRoute}
                  handleDeleteItem={this.deleteReport}
                  handleCloneItem={this.cloneReport}
                  handleSetUpdating={this.handleSetUpdating}
                  renderCustomToolbar={this.renderCustomToolbar}
                  handleNameLinkClick={() => this.trackEvent('Report List - Open Report')}
                  readRoleName={'read_report'}
                  writeRoleName={'write_report'}
                />
              </Grid>

              {/* recipients dialog */}
              <Dialog
                fullWidth
                maxWidth='md'
                keepMounted
                open={openRecipientsDialog}
                onClose={this.handleCloseRecipientsDialog}
              >
                <ModalSelectTable
                  title=''
                  items={selectedReport.Recipients.map((recipient, idx) => {
                    return {
                      ID: idx,
                      Name: recipient,
                    };
                  })}
                  interactive={false}
                  enableSearchBarLeft={true}
                  renderCustomToolbar={this.renderRecipientsModalToolbar}
                />
              </Dialog>
              <SnackbarView
                open={!!snackbarMessage}
                error={!!snackbarError}
                onClose={this.snackbarClose}
              >
                {snackbarMessage}
              </SnackbarView>
            </Grid>
          </Panel>
        </Panels>
      );
    }
  },
);
